<template>
  <el-form
    ref="formRef"
    require-asterisk-position="right"
    label-position="top"
    :model="formValues"
    :rules="rules"
  >
    <div style="display: flex">
      <div class="oa-form-left">
        <el-form-item prop="bankAccountOpenLicenseUrl" label="银行开户许可证">
          <card-image-upload
            :action="UploadImageUrls.normal"
            :image="formValues.bankAccountOpenLicenseUrl"
            :readonly="disabled"
            @on-success="(res: any) => {
              formValues.bankAccountOpenLicenseUrl = res.picUrl;
              formRef?.validateField?.('bankAccountOpenLicenseUrl');
            }"
          />
        </el-form-item>
        <el-form-item prop="bankAccountName" label="对公账户名">
          <el-input
            v-model="formValues.bankAccountName"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            :disabled="disabled"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
        <el-form-item prop="creditCode" label="纳税人识别号">
          <el-input
            v-model="formValues.creditCode"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            :disabled="disabled"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
        <el-form-item prop="phoneNo" :label="phoneFieldLabel">
          <el-input
            v-model="formValues.phoneNo"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            :disabled="disabled"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
      </div>

      <div class="oa-form-right">
        <el-form-item label="xx" style="opacity: 0;margin-bottom: 189px;">
          &nbsp;
        </el-form-item>
        <el-form-item prop="bankNo" label="银行账号">
          <el-input
            v-model="formValues.bankNo"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            :disabled="disabled"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
        <el-form-item prop="address" label="开票地址">
          <el-input
            v-model="formValues.address"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            :disabled="disabled"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
      </div>
    </div>

    <!-- 选择开户行 -->
    <el-form-item label="开户银行" required style="height: 10px;">
      &nbsp;
    </el-form-item>
    <div class="flex w-full items-center acc-one-row">
      <el-form-item prop="bankCode">
        <el-select
          v-model="formValues.bankCode"
          class="w-[190px]"
          clearable
          filterable
          placeholder="请选择开户银行"
          autocomplete="noy"
          :filter-method="filterBankCode"
          @change="selectBasicBank"
          @keyup.enter="onKeyupEnter"
        >
          <el-option
            v-for="item in basicBankList"
            :key="item.node.id"
            :label="item.node.bank_name"
            :value="item.node.bank_code"
          />
        </el-select>
      </el-form-item>
      <el-form-item prop="cityCode" class="!w-[300px]">
        <area-select
          :initial-codes="initialAreaCodes"
          :enable-town="false"
          prov-placeholder="请选择开户省份"
          city-placeholder="请选择开户城市"
          @key-enter="onKeyupEnter"
          @change="(vals: string[]) => {
            formValues.provinceCode = vals[0];
            formValues.cityCode = vals[1];
            onCityChange();
          }"
        />
      </el-form-item>
      <el-form-item v-if="!customWriteBranchBank" prop="bankBranchNo">
        <el-select
          v-model="formValues.bankBranchNo"
          class="w-[360px]"
          filterable
          clearable
          autocomplete="noy"
          remote
          placeholder="输入关键字搜索并选择开户支行"
          :remote-method="queryBankBranchSearch"
          @change="setBranchBySelectedCode"
          @keyup.enter="onKeyupEnter"
        >
          <el-option
            v-for="item in bankBranchList"
            :key="item.cnaps_code"
            :label="item.brabank_name"
            :value="item.cnaps_code"
          />
        </el-select>
      </el-form-item>
      <template v-else>
        <el-form-item prop="customBankBranchName">
          <el-input
            v-model="formValues.customBankBranchName"
            class="w-[160px]"
            clearable
            autocomplete="nony"
            placeholder="请输入开户支行名称"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
        <el-form-item prop="cnapsCode">
          <el-input
            v-model="formValues.cnapsCode"
            class="w-[160px]"
            clearable
            autocomplete="nony"
            placeholder="请输入大额行号"
            @keyup.enter="onKeyupEnter"
          />
        </el-form-item>
      </template>
      <el-button class="top-[-10px] relative" plain type="warning" @click="toggleCustomStatus">
        {{ !customWriteBranchBank ? '其他支行' : '选择开户支行' }}
      </el-button>
    </div>
  </el-form>
</template>

<script setup lang="ts">
import { CardImageUpload, AreaSelect } from '@vue3/components';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { computed, PropType, ref, toRaw, watch } from 'vue';
import { IAccOpening } from './type';
import { bankCode as banks, checkPhone, scrollToErrorField } from '@core/tools';
import { ICnapsCode } from '../../../../types/lianlian';
import { queryCnapsCodes } from '../../../../api/lianlian';
import { UploadImageUrls } from '../../../../api/common';

const props = defineProps({
  data: {
    type: Object as PropType<IAccOpening>,
    default: () => {
      return {} as IAccOpening;
    },
  },
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  /** 自动滚动到错误位置 */
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
  disableLicense: {
    type: Boolean,
    default: false,
  },
  phoneFieldLabel: {
    type: String,
    default: '电话号码',
  },
});

const emit = defineEmits<{'key-enter': []}>();

const formRef = ref<FormInstance>();

const formValues = ref({} as IAccOpening);
const basicBankList = ref(banks);
const bankBranchList = ref<ICnapsCode[]>([]);
const initialAreaCodes = ref<string[]>([]);

/** 手动填写开户支行名称，开户支行大额行号 */
const customWriteBranchBank = computed(() => Number(formValues.value.customStatus ?? '') === 1);
const disabled = computed(() => props.disableLicense);

const rules = computed<FormRules<IAccOpening>>(() => {
  return {
    bankAccountOpenLicenseUrl:
      !disabled.value
        ?
        [
          {
            required: true,
            message: '请上传银行开户许可证',
          },
        ]
        : undefined,
    bankAccountName:
      !disabled.value
        ?
        [
          {
            required: true,
            message: '请输入对公账户名',
          },
        ]
        : undefined,
    creditCode:
      !disabled.value
        ?
        [
          {
            required: true,
            message: '请输入纳税人识别号',
          },
        ]
        : undefined,
    address:
      !disabled.value
        ?
        [
          {
            required: true,
            message: '请输入注册地址',
          },
        ]
        : undefined,
    phoneNo:
      !disabled.value
        ?
        [
          {
            required: true,
            validator: (rule: any, value: string, callback: any) => {
              if (checkPhone(formValues.value.phoneNo)) {
                callback(checkPhone(formValues.value.phoneNo));
              }
              callback();
            },
          },
        ]
        : undefined,
    bankCode: [
      {
        required: true,
        message: '请选择开户银行',
      },
    ],
    bankNo:
      !disabled.value
        ?
        [
          {
            required: true,
            message: '请输入银行账号',
          },
        ]
        : undefined,
    cityCode: [
      {
        required: true,
        message: '请选择开户城市所在省市',
      },
    ],
    bankBranchNo: [
      {
        required: true,
        message: '输入关键字搜索并选择开户支行',
      },
    ],
    customBankBranchName: [
      {
        required: true,
        message: '请输入开户支行名称',
      },
    ],
    cnapsCode: [
      {
        required: true,
        message: '请输入大额行号',
      },
    ],
  };
});

function filterBankCode(name: string) {
  basicBankList.value = banks.filter((item) => item.node.bank_name.includes(name));
}

async function validate(callback?: () => void) {
  let valid = false;

  if (!formRef.value) return valid;

  await formRef.value.validate((v) => {
    if (v) {
      valid = true;
      callback && callback();
    } else {
      if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    }
  });

  return valid;
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  const valid = await validate();

  if (!valid) {
    if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    return;
  }

  emit('key-enter');
}

function selectBasicBank(bankcode: string) {
  const list = banks || [];
  const result = list ? list.filter((v) => v.node.bank_code === bankcode) : [];

  if (result.length > 0) {
    const { node } = result[0];

    // 开户银行赋值（名称和code）
    formValues.value.bankCode = node.bank_code;
    formValues.value.brabankName = node.bank_name;
  }

  bankBranchList.value = [];
  initialAreaCodes.value = [];
  formValues.value.cityCode = '';
  formValues.value.provinceCode = '';
  formRef.value?.validateField?.('cityCode');

  onCityChange();
}

function setBranchBySelectedCode(code: string) {
  const banks = [...bankBranchList.value];
  const results = banks.filter((v) => v.cnaps_code === code) || [];

  if (results.length > 0) {
    resetCustomeBankBranch();
    formValues.value.bankBranchNo = code;
    formValues.value.relationBranchBankName = results[0].brabank_name;
  }
}

async function queryBankBranchSearch(queryString: string) {
  if (!formValues.value.bankCode) return;
  if (!formValues.value.cityCode) return;
  if (!queryString.trim() && !bankBranchList.value.length) return;

  if (!queryString.trim()) return;

  const data = {
    brabankName: queryString,
    bankCode: formValues.value.bankCode,
    cityCode: formValues.value.cityCode,
  };

  try {
    bankBranchList.value = await queryCnapsCodes(data);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message || '查询开户支行列表失败');
  }
}

function resetCustomeBankBranch() {
  formValues.value.cnapsCode = '';
  formValues.value.customBankBranchName = '';
}

function toggleCustomStatus() {
  const status = formValues.value.customStatus ?? 0;

  formValues.value.customStatus = status === 0 ? 1 : 0;
}

function initBankBranchList() { // 需要根据bankBranchNo进行数据回显
  const d = toRaw(formValues.value);

  if (
    d.customStatus === 0
    && d.bankBranchNo
    && d.bankCode
    && d.cityCode
  ) {
    queryBankBranchSearch('1');
    setBranchBySelectedCode(d.bankBranchNo);
  }

  if (d.cityCode) {
    initialAreaCodes.value = [d.provinceCode, d.cityCode];
  }
}

function onCityChange() {
  resetBranchBanks();
  resetCustomeBankBranch();
  formValues.value.relationBranchBankName = '';
}

function resetBranchBanks() {
  // 重新查询"开户支行"列表
  bankBranchList.value = [];
  formValues.value.bankBranchNo = '';
}

function resetFormValues() {
  const d = JSON.parse(JSON.stringify(props.data)) as IAccOpening;

  if (!d.phoneNo) d.phoneNo = d.linkedPhone; // 兼容资金账户 - 连连账户

  formRef.value?.resetFields?.();
  formValues.value = {
    customStatus: d.customStatus || 0,
    bankAccountOpenLicenseUrl: d.bankAccountOpenLicenseUrl,
    bankAccountName: d.bankAccountName,
    creditCode: d.creditCode,
    address: d.address,
    phoneNo: d.phoneNo,
    bankCode: d.bankCode,
    customBankBranchName: d.customBankBranchName,
    bankBranchNo: d.bankBranchNo,
    bankNo: d.bankNo,
    cnapsCode: d.cnapsCode,
    provinceCode: d.provinceCode,
    cityCode: d.cityCode,
  };

  initBankBranchList();
}

watch(
  () => props.data,
  () => {
    resetFormValues();
  },
  { immediate: true },
);

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  /** 获取表单数据 */
  getValues: () => {
    const vals = toRaw(formValues.value);
    const isCustom = vals.customStatus === 1;

    const bankBranch = bankBranchList.value.find((v) => v.cnaps_code === formValues.value.bankBranchNo || formValues.value.cnapsCode);

    return {
      ...vals,
      brabankName: basicBankList.value?.find((v) => v.node.bank_code === vals.bankCode)?.node?.bank_name || undefined,
      customBankBranchName: isCustom ? vals.customBankBranchName : undefined,
      relationBranchBankName: !isCustom ? bankBranch?.brabank_name : undefined,
    };
  },
  /** 表单数据重置（base props.data） */
  reset: resetFormValues,
});
</script>

<style scoped lang="scss" src="../index.scss" />

